/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import appTiles from './assets/twc-app-tiles.svg';

const styles = cssMap({
	root: {
		width: '100%',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		// Horizontal margins were causing the banner to exceed the side-nav width
		// (width: 100% + horizontal margins overflows the container). Keep only a
		// minimal vertical gap from the surrounding nav items.
		marginInline: 0,
		marginBlock: token('space.050'),
	},
	appTilesWrapper: {
		marginTop: token('space.150'),
	},
	headerRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.150'),
	},
});

const messages = defineMessages({
	heading: {
		id: 'twc.sidenav.banner.generic.heading',
		defaultMessage: 'Explore Teamwork Collection',
		description: 'Main heading for the generic TWC activation banner in the product sidenav.',
	},
	subheadingJira: {
		id: 'twc.sidenav.banner.generic.subheading.jira',
		defaultMessage: 'Discover how the other apps can accelerate your workflows in Jira.',
		description: 'Subheading for the generic banner when shown in Jira.',
	},
	subheadingConfluence: {
		id: 'twc.sidenav.banner.generic.subheading.confluence',
		defaultMessage: 'Discover how the other apps can accelerate your workflows in Confluence.',
		description: 'Subheading for the generic banner when shown in Confluence.',
	},
	appTilesAlt: {
		id: 'twc.sidenav.banner.generic.app.tiles.alt',
		defaultMessage: 'App tiles illustration',
		description: 'Alt text for the app tiles illustration in the generic banner.',
	},
	ctaButton: {
		id: 'twc.sidenav.banner.generic.cta',
		defaultMessage: 'Try now',
		description: 'CTA button text for the generic TWC activation banner in the product sidenav.',
	},
	closeLabel: {
		id: 'twc.sidenav.banner.generic.close',
		defaultMessage: 'Close',
		description: 'Accessible label for the close button on the generic TWC activation banner.',
	},
});

type SurfaceProduct = 'jira' | 'confluence';

export type GenericBannerProps = {
	heading?: string;
	href: string;
	locale?: string;
	product: 'jira' | 'confluence';
	messageInstanceId?: string;
	onClose?: () => void;
	onCtaClick?: () => void;
	surfaceProduct?: SurfaceProduct;
};

export const GenericBanner = ({
	heading,
	href,
	locale,
	product,
	messageInstanceId,
	onClose,
	onCtaClick,
	surfaceProduct,
}: GenericBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcGenericBanner',
			attributes: {
				product,
				surfaceProduct,
				messageInstanceId,
				locale,
				arm: 'generic' as const,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, product, surfaceProduct, messageInstanceId, locale]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcGenericBannerClose',
			attributes: {
				product,
				surfaceProduct,
				messageInstanceId,
				locale,
				arm: 'generic' as const,
			},
		}).fire('growth');
		onClose?.();
	};

	const handleCtaClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcGenericBannerCta',
			attributes: {
				product,
				surfaceProduct,
				messageInstanceId,
				locale,
				arm: 'generic' as const,
			},
		}).fire('growth');
		onCtaClick?.();
	};

	return (
		<Box xcss={styles.root} testId="twc-generic-banner">
			<Stack space="space.150">
				<Box xcss={styles.headerRow}>
					<Box xcss={styles.appTilesWrapper}>
						<Image
							src={appTiles}
							srcDark={appTiles}
							alt={formatMessage(messages.appTilesAlt)}
							testId="twc-generic-banner-app-tiles"
							width={91}
						/>
					</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={handleClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-generic-banner-close"
						appearance="subtle"
					/>
				</Box>
				<Stack space="space.050">
					<Heading as="h3" size="xsmall" testId="twc-generic-banner-heading">
						{heading ?? formatMessage(messages.heading)}
					</Heading>
					<Text
						as="p"
						size="small"
						color="color.text.subtle"
						maxLines={2}
						testId="twc-generic-banner-subheading"
					>
						{product === 'jira'
							? formatMessage(messages.subheadingJira)
							: formatMessage(messages.subheadingConfluence)}
					</Text>
				</Stack>
				<LinkButton
					shouldFitContainer
					href={href}
					testId="twc-generic-banner-cta"
					onClick={handleCtaClick}
					target="_blank"
				>
					{formatMessage(messages.ctaButton)}
				</LinkButton>
			</Stack>
		</Box>
	);
};
